/*
 * COPYRIGHT © 2026 SANITÀNEXUS WHITE-LABEL. ALL RIGHTS RESERVED.
 * AUTHOR AND SOLE DEVELOPER: MARCO PANZETTI (C.F. PNZMRC89M14F251C)
 * Protected under Italian Copyright Law (Legge 633/1941) and International Treaties.
 * Unauthorized copying, reverse engineering, or distribution of this software architecture,
 * UI/UX layouts, local-first logic, or system prompt configurations is strictly prohibited.
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { Database, Download, Upload, CheckCircle2, XCircle, Loader2 } from 'lucide-react';
import { exportFhirBundle, importFhirBundle, getFhirReadiness } from '@/lib/sprint5';

const FhirPage = () => {
  const [bundle, setBundle] = useState(null);
  const [readiness, setReadiness] = useState(null);
  const [exporting, setExporting] = useState(false);
  const [importing, setImporting] = useState(false);
  const [dryRun, setDryRun] = useState(true);
  const [importStats, setImportStats] = useState(null);
  const inputRef = useRef(null);

  const load = useCallback(async () => {
    try {
      const r = await getFhirReadiness();
      setReadiness(r);
    } catch (e) { /* auth */ }
  }, []);
  useEffect(() => { load(); }, [load]);

  const doExport = async () => {
    setExporting(true);
    try {
      const res = await exportFhirBundle();
      setBundle(res.bundle);
      // Trigger browser download
      const blob = new Blob([JSON.stringify(res.bundle, null, 2)], { type: 'application/fhir+json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `fhir-bundle-${new Date().toISOString().slice(0, 10)}.json`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success(`${res.resource_count} risorse FHIR esportate`);
    } catch (err) { toast.error(err?.response?.data?.detail || 'Errore export'); }
    finally { setExporting(false); }
  };

  const doImport = async (file) => {
    if (!file) return;
    setImporting(true); setImportStats(null);
    try {
      const text = await file.text();
      const parsed = JSON.parse(text);
      const res = await importFhirBundle(parsed, dryRun);
      setImportStats(res.imported);
      toast.success(`Import completato ${dryRun ? '(dry-run)' : ''}`);
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Errore import');
    } finally { setImporting(false); }
  };

  return (
    <div className="max-w-4xl mx-auto p-4 md:p-6 space-y-6" data-testid="fhir-page">
      <div>
        <h1 className="font-display text-2xl md:text-3xl font-bold flex items-center gap-2">
          <Database className="h-6 w-6 text-primary" /> Fascicolo Sanitario &amp; FHIR
        </h1>
        <p className="text-sm text-muted-foreground mt-1">
          Esporta i tuoi dati sanitari in formato standard <b>HL7 FHIR R4</b> per portarli sul Fascicolo Sanitario Elettronico o presso altri operatori. Zero lock-in, i tuoi dati sono tuoi.
        </p>
      </div>

      {/* Readiness */}
      <section className="space-y-3">
        <h2 className="font-display font-semibold text-lg">Prepara per il FSE</h2>
        {!readiness ? <Skeleton className="h-40" /> : (
          <Card><CardContent className="p-4 space-y-3">
            <div className="flex items-center gap-3">
              <div className="text-3xl font-bold font-display">{readiness.score}%</div>
              <div className="flex-1">
                <div className="font-semibold text-sm">Punteggio FSE-ready</div>
                <div className="text-xs text-muted-foreground">Più dati completi → più utile l'export nel FSE.</div>
              </div>
            </div>
            <div className="h-2 rounded-full bg-muted overflow-hidden">
              <div className="h-full bg-primary-gradient transition-all" style={{ width: `${readiness.score}%` }} />
            </div>
            <ul className="space-y-1 mt-2">
              {readiness.checks.map((c, i) => (
                <li key={`check-${i}-${c.label.slice(0,10)}`} className="flex items-center gap-2 text-xs">
                  {c.ok ? <CheckCircle2 className="h-3.5 w-3.5 text-emerald-600" /> : <XCircle className="h-3.5 w-3.5 text-muted-foreground" />}
                  <span className={c.ok ? '' : 'text-muted-foreground'}>{c.label}</span>
                </li>
              ))}
            </ul>
            <p className="text-[10px] italic text-muted-foreground border-t pt-2">{readiness.disclaimer}</p>
          </CardContent></Card>
        )}
      </section>

      {/* Export */}
      <section className="space-y-3">
        <h2 className="font-display font-semibold text-lg">Esporta i tuoi dati</h2>
        <Card><CardContent className="p-4 space-y-3">
          <p className="text-sm text-muted-foreground">
            Genera un <b>FHIR Bundle</b> R4 contenente il tuo profilo, misurazioni IoT, prescrizioni, condizioni cliniche, allergie e documenti. Formato standard usato dal FSE nazionale e dai portali regionali.
          </p>
          <Button onClick={doExport} disabled={exporting} data-testid="fhir-export-btn">
            {exporting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />} Scarica FHIR Bundle
          </Button>
          {bundle && (
            <div className="text-xs text-muted-foreground">
              Bundle generato: <b>{(bundle.entry || []).length}</b> risorse.
            </div>
          )}
        </CardContent></Card>
      </section>

      {/* Import */}
      <section className="space-y-3">
        <h2 className="font-display font-semibold text-lg">Importa dati dal FSE</h2>
        <Card><CardContent className="p-4 space-y-3">
          <p className="text-sm text-muted-foreground">
            Se hai scaricato il tuo FSE dal portale regionale in formato JSON, puoi importarlo qui. I dati verranno uniti (nessuna sovrascrittura).
          </p>
          <div className="flex items-center gap-2">
            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" checked={dryRun} onChange={(e) => setDryRun(e.target.checked)} data-testid="fhir-dryrun-checkbox" />
              Modalità dry-run (simulazione senza scrivere)
            </label>
          </div>
          <input ref={inputRef} type="file" accept="application/json,application/fhir+json" className="hidden" onChange={(e) => doImport(e.target.files?.[0])} />
          <Button onClick={() => inputRef.current?.click()} disabled={importing} data-testid="fhir-import-btn">
            {importing ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />} Carica Bundle
          </Button>
          {importStats && (
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mt-3" data-testid="fhir-import-stats">
              <StatCard label="Osservazioni" value={importStats.observations} />
              <StatCard label="Farmaci" value={importStats.medications} />
              <StatCard label="Condizioni" value={importStats.conditions} />
              <StatCard label="Allergie" value={importStats.allergies} />
            </div>
          )}
        </CardContent></Card>
      </section>
    </div>
  );
};

const StatCard = ({ label, value }) => (
  <Card><CardContent className="p-3 text-center">
    <div className="text-xl font-bold font-display">{value}</div>
    <div className="text-[10px] uppercase text-muted-foreground">{label}</div>
  </CardContent></Card>
);

export default FhirPage;
